import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { ShieldAlert } from 'lucide-react';
import { Button, Input, toast } from '@cyphex/ui';
import { isApiRequestError } from '@/lib/api/errors';
import { selfSecurityApi } from '@/features/admin/api/securityApi';
import { useAuth } from '../hooks/useAuth';
import { AuthShell } from './AuthShell';

/**
 * Payload bounds only. The REAL rules — length, complexity, denylist, reuse
 * against remembered history, minimum age — are the tenant's policy and are
 * decided server-side; duplicating them here would drift and start rejecting
 * passwords the server would have accepted.
 */
const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter the password you just signed in with.').max(512),
    newPassword: z.string().min(8, 'Use at least 8 characters.').max(512),
    confirmPassword: z.string().min(1, 'Repeat the new password.').max(512),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Those passwords do not match.',
    path: ['confirmPassword'],
  })
  .refine((values) => values.newPassword !== values.currentPassword, {
    message: 'Choose a password you have not just used.',
    path: ['newPassword'],
  });

type ForceValues = z.infer<typeof schema>;

/**
 * The full-screen interruption shown when the signed-in session is flagged
 * `must_change_password` — an administrator issued a temporary password, or the
 * tenant's expiry policy came due.
 *
 * THIS IS THE ESCAPE HATCH, NOT THE CONTROL. The API refuses that session on
 * every route except the change-password endpoints, so rendering the normal
 * shell here would only produce a screen full of 403s. Blocking in the client
 * is what turns a correct-but-hostile API response into a usable instruction.
 *
 * There is deliberately no "skip" affordance: the only ways out are completing
 * the form or signing out.
 */
export function ForcePasswordChange() {
  const { signOut, passwordChanged } = useAuth();

  const form = useForm<ForceValues>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: ForceValues) =>
      selfSecurityApi.changePassword(values.currentPassword, values.newPassword),
    onSuccess: () => {
      toast.success('Password updated. Your other devices were signed out.');
      passwordChanged();
    },
    onError: (error: unknown) => {
      // The policy failure text is the server's, verbatim: it names the rule
      // that was broken, which a generic message cannot.
      const message =
        isApiRequestError(error) && [400, 422].includes(error.status)
          ? error.message
          : 'We could not change your password. Try again.';
      form.setError('newPassword', { message });
    },
  });

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="This password was issued to you and has to be replaced before you can carry on."
      footer={
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => {
            void signOut();
          }}
        >
          Sign out instead
        </button>
      }
    >
      <div className="mb-5 flex gap-3 rounded-lg border border-border bg-muted p-3.5">
        <ShieldAlert size={16} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
        <p className="text-base text-muted-foreground">
          Until this is done, the rest of the workspace stays locked.
        </p>
      </div>

      <form
        className="space-y-3"
        onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
      >
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          error={form.formState.errors.currentPassword?.message}
          {...form.register('currentPassword')}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          hint="Your workspace sets the minimum length and complexity."
          error={form.formState.errors.newPassword?.message}
          {...form.register('newPassword')}
        />
        <Input
          label="Repeat new password"
          type="password"
          autoComplete="new-password"
          error={form.formState.errors.confirmPassword?.message}
          {...form.register('confirmPassword')}
        />

        <Button type="submit" variant="primary" size="md" loading={mutation.isPending} className="w-full">
          Update password and continue
        </Button>
      </form>
    </AuthShell>
  );
}
